import { ApplyMode, WorkfrontConfiguredIdKind, WorkfrontScopeKey, WorkfrontStreamCode } from '@atlas/types'
import { z } from 'zod'

/** A Workfront object ID: 32 lowercase hexadecimal characters. */
export const workfrontIdSchema = z.string().regex(/^[0-9a-f]{32}$/, 'must be a 32-character Workfront ID')

/** A Workfront status code, such as `CUR` or a custom `ALM`. */
const statusCodeSchema = z.string().regex(/^[A-Z0-9]{1,6}$/, 'must be a Workfront status code')

/** Field names as Workfront's `fields` parameter takes them: `name`, `DE:MAH | Brief Type`, `owner:name`. */
const fieldListSchema = z.array(z.string().trim().min(1)).max(300)

/** Which statuses close an object: codes listed explicitly, or any status that equates to one of these. */
const closedStatusesSchema = z
	.object({
		codes: z.array(statusCodeSchema),
		equatesWith: z.array(statusCodeSchema),
	})
	.strict()

/** The parameter groups a description is composed from, per level, and the fields always left out. */
const descriptionGroupsSchema = z
	.object({
		parent: z.array(workfrontIdSchema),
		generator: z.array(workfrontIdSchema),
		child: z.array(workfrontIdSchema),
		exclude: z.array(workfrontIdSchema),
	})
	.strict()

// ─── Scope values ───────────────────────────────────────────────────────────
// One schema per key. A value that does not match its key's schema is refused,
// so a typo in a flag or a cadence never reaches a run.

export const workfrontScopeValueSchemas: Record<WorkfrontScopeKey, z.ZodType> = {
	[WorkfrontScopeKey.ADAPTER_ENABLED]: z.boolean(),
	[WorkfrontScopeKey.REFERENCE_SCHEDULE_ENABLED]: z.boolean(),
	[WorkfrontScopeKey.BRIEF_TYPES]: z.array(z.string().trim().min(1)),
	[WorkfrontScopeKey.CLOSED_STATUSES_PROJ]: closedStatusesSchema,
	[WorkfrontScopeKey.CLOSED_STATUSES_TASK]: closedStatusesSchema,
	[WorkfrontScopeKey.FIELD_LISTS_PROJ]: fieldListSchema,
	[WorkfrontScopeKey.FIELD_LISTS_OPTASK]: fieldListSchema,
	[WorkfrontScopeKey.FIELD_LISTS_TASK]: fieldListSchema,
	[WorkfrontScopeKey.FIELD_LISTS_DOCU]: fieldListSchema,
	[WorkfrontScopeKey.FIELD_LISTS_NOTE]: fieldListSchema,
	[WorkfrontScopeKey.CADENCE_DISCOVERY_MINUTES]: z.number().int().min(5).max(1440),
	[WorkfrontScopeKey.CADENCE_FULL_REFETCH_HOUR_UTC]: z.number().int().min(0).max(23),
	[WorkfrontScopeKey.FINGERPRINT_EXCLUSIONS]: z.array(z.string().trim().min(1)).max(50),
	[WorkfrontScopeKey.REOPEN_WINDOW_DAYS]: z.number().int().min(1).max(365),
	[WorkfrontScopeKey.DISCOVERY_BUDGET_SECONDS]: z.number().int().min(60).max(840),
	[WorkfrontScopeKey.DISCOVERY_CHUNK_SIZE]: z.number().int().min(1).max(100),
	[WorkfrontScopeKey.BUNDLES_ON_DEMAND_ENABLED]: z.boolean(),
	[WorkfrontScopeKey.DISCOVERY_HOURLY_ENABLED]: z.boolean(),
	[WorkfrontScopeKey.DISCOVERY_NIGHTLY_ENABLED]: z.boolean(),
	[WorkfrontScopeKey.BUNDLE_REQUESTS_WARNING]: z.number().int().min(1).max(10_000),
	[WorkfrontScopeKey.BUNDLE_SECONDS_WARNING]: z.number().int().min(1).max(900),
	[WorkfrontScopeKey.MAPPING_ENABLED]: z.boolean(),
	[WorkfrontScopeKey.REMAP_BATCH_PER_MINUTE]: z.number().int().min(1).max(500),
	[WorkfrontScopeKey.DESCRIPTION_GROUPS]: descriptionGroupsSchema,
	[WorkfrontScopeKey.APPLY_MODE]: z.enum(ApplyMode),
	[WorkfrontScopeKey.APPLY_SLICE_SIZE]: z.number().int().min(1).max(100),
	[WorkfrontScopeKey.APPLY_BACKFILL_SINCE]: z.iso.date().nullable(),
	[WorkfrontScopeKey.WORK_ORDER_PAGE_REFRESH_ENABLED]: z.boolean(),
}

/** `PUT /admin/workfront/scope/{key}`: the value is checked against the key's own schema. */
export const workfrontScopeUpdateSchema = z.object({ value: z.unknown() }).strict()

// ─── Configured IDs ─────────────────────────────────────────────────────────

/** A role: a well-known scope role or a field's own name, in snake_case. */
const roleSchema = z.string().regex(/^[a-z][a-z0-9_]{1,62}$/, 'must be snake_case, 2 to 63 characters')

export const workfrontConfiguredIdDescriptionSchema = z
	.object({
		objCode: z.string().regex(/^[A-Z]{3,8}$/, 'must be a Workfront object code'),
		name: z.string().trim().min(1),
		label: z.string().trim().min(1).optional(),
		dataType: z.string().min(1).optional(),
		displayType: z.string().min(1).optional(),
		catObjCode: z.string().min(1).optional(),
		templateId: workfrontIdSchema.optional(),
		enumClass: z.string().min(1).optional(),
		code: statusCodeSchema.optional(),
		equatesWith: statusCodeSchema.optional(),
		forms: z.array(workfrontIdSchema).optional(),
	})
	.strict()

export const workfrontConfiguredIdCreateSchema = z
	.object({
		kind: z.enum(WorkfrontConfiguredIdKind),
		role: roleSchema,
		workfrontId: workfrontIdSchema,
		description: workfrontConfiguredIdDescriptionSchema,
	})
	.strict()

// ─── Environment resolve ────────────────────────────────────────────────────

/** `POST /admin/workfront/environment/resolve`: the missing IDs to propose for; all missing ones when omitted. */
export const workfrontResolveRequestSchema = z
	.object({ configuredIdIds: z.array(z.uuid()).min(1).max(100).optional() })
	.strict()

/** `POST /admin/workfront/environment/resolve/confirm`: point a configured ID at the chosen Workfront object. */
export const workfrontResolveConfirmSchema = z
	.object({ configuredIdId: z.uuid(), workfrontId: workfrontIdSchema })
	.strict()

// ─── Errors ─────────────────────────────────────────────────────────────────

/** `POST /admin/workfront/errors/{id}/ignore`: ignoring is a decision, so it needs a reason. */
export const workfrontErrorIgnoreSchema = z.object({ reason: z.string().trim().min(3).max(1000) }).strict()

// ─── Bundle sync ────────────────────────────────────────────────────────────

/** One bundle, by any reference to it: a Workfront reference number, one of its projects, or its program. */
const workfrontBundleReferenceSchema = z.union([
	z.object({ referenceNumber: z.number().int().positive() }).strict(),
	z.object({ projectId: workfrontIdSchema }).strict(),
	z.object({ programId: workfrontIdSchema }).strict(),
])

/** `POST /workfront/sync`: a discovery run now (`all`), or one bundle fetched now (`bundle`). */
export const workfrontSyncRequestSchema = z.discriminatedUnion('mode', [
	z.object({ mode: z.literal('all'), streams: z.array(z.enum(WorkfrontStreamCode)).min(1).optional() }).strict(),
	z.object({ mode: z.literal('bundle'), reference: workfrontBundleReferenceSchema }).strict(),
])

/** `POST /admin/workfront/streams/{code}/cursor`: the stream reads again from this moment. */
export const workfrontStreamCursorSchema = z.object({ date: z.iso.datetime({ offset: true }) }).strict()

export type WorkfrontScopeUpdateInput = z.infer<typeof workfrontScopeUpdateSchema>
export type WorkfrontSyncRequestInput = z.infer<typeof workfrontSyncRequestSchema>
export type WorkfrontBundleReferenceInput = z.infer<typeof workfrontBundleReferenceSchema>
export type WorkfrontStreamCursorInput = z.infer<typeof workfrontStreamCursorSchema>
export type WorkfrontConfiguredIdCreateInput = z.infer<typeof workfrontConfiguredIdCreateSchema>
export type WorkfrontResolveRequestInput = z.infer<typeof workfrontResolveRequestSchema>
export type WorkfrontResolveConfirmInput = z.infer<typeof workfrontResolveConfirmSchema>
export type WorkfrontErrorIgnoreInput = z.infer<typeof workfrontErrorIgnoreSchema>
